import { useCallback } from 'react';
import { useMemo } from 'react';

import { TierStatusName } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import type {
  AccountSweetpassBillingHistorySection,
  AccountSweetpassCancellationSection,
  AccountSweetpassMembershipSection,
  AccountSweetpassPaymentMethodsSection,
} from '../useSweetpassAccountContentfulData';
import {
  ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION,
  ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION,
  ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION,
  useSweetpassAccountContentfulData,
} from '../useSweetpassAccountContentfulData';
import { useSweetpassMembershipData } from '../useSweetpassMembershipData';
import {
  getBillingHistorySection,
  getCancellationSection,
  getMembershipLevelSection,
  getPaymentMethodsSection,
} from './utils';

export const useSweetpassMembershipContent = (
  props: UseSweetpassMembershipContentProps,
) => {
  const { track } = useTelemetry();
  const {
    formatMessage,
    formatDurationUnitName,
    formatDistanceBetweenDatesInDays,
  } = useLocalizationContext();
  const { content, isLoadingContentfulData } =
    useSweetpassAccountContentfulData();

  const {
    subscribeHandler,
    viewPaymentMethodHandler,
    addPaymentMethodHandler,
    viewBillingHistoryHandler,
  } = props;

  const {
    cancelSubscriptionAtCycleEnd,
    reactivateSubscription,
    planPrice,
    billingFrequencyUnit,
    ...sweetpassMembershipResult
  } = useSweetpassMembershipData();

  const isFreeTrial =
    sweetpassMembershipResult.tierStatus === TierStatusName.FreeTrial;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const cancelSubscriptionHandler = useCallback(async () => {
    await cancelSubscriptionAtCycleEnd();
    track('sweetpass_account.cancel_cta.tapped', {
      isFreeTrial,
      isMonthlySubscription: true,
      isAnnualSubscription: false,
    });
  }, [isFreeTrial, cancelSubscriptionAtCycleEnd, track]);

  const reactivateSubscriptionHandler = useCallback(async () => {
    // Is there a reactivate subscription confirmation modal?
    track('sweetpass_account.rejoin_cta.tapped');
    await reactivateSubscription();
  }, [reactivateSubscription, track]);

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    const { isSweetpassMembershipLoading, loyaltyProfileError, ...membership } =
      sweetpassMembershipResult;

    const isSweetpassContentLoading =
      isSweetpassMembershipLoading || isLoadingContentfulData;

    const hasMembershipError = Boolean(loyaltyProfileError) || !membership.tier;

    if (isSweetpassContentLoading || hasMembershipError) {
      return {
        ...sweetpassMembershipResult,
        isSweetpassContentLoading,
        hasMembershipError,
        sweetpassMembershipContent: [],
      };
    }

    return {
      ...sweetpassMembershipResult,
      isSweetpassContentLoading,
      hasMembershipError,
      sweetpassMembershipContent: content?.map((section) => ({
        // ─── Membership Section ───────────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION
          ? getMembershipLevelSection({
              ...(section as AccountSweetpassMembershipSection),
              ...sweetpassMembershipResult,
              planPrice,
              billingFrequencyUnit,
              subscribeHandler,
              reactivateSubscriptionHandler,
              formatMessage,
              formatDurationUnitName,
              formatDistanceBetweenDatesInDays,
            })
          : {}),

        // ─── Cancellation Section ─────────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_CANCELLATION_SECTION
          ? getCancellationSection({
              ...(section as AccountSweetpassCancellationSection),
              ...sweetpassMembershipResult,
              cancelSubscriptionHandler,
              formatMessage,
            })
          : {}),

        // ─── Payment Methods Section ──────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION
          ? getPaymentMethodsSection({
              ...(section as AccountSweetpassPaymentMethodsSection),
              ...sweetpassMembershipResult,
              planPrice,
              billingFrequencyUnit,
              viewPaymentMethodHandler,
              addPaymentMethodHandler,
              formatMessage,
            })
          : {}),

        // ─── Billing History Section ──────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION
          ? getBillingHistorySection({
              ...(section as AccountSweetpassBillingHistorySection),
              ...sweetpassMembershipResult,
              planPrice,
              billingFrequencyUnit,
              viewBillingHistoryHandler,
              formatMessage,
            })
          : {}),
      })),
    };
  }, [
    sweetpassMembershipResult,
    isLoadingContentfulData,
    content,
    planPrice,
    billingFrequencyUnit,
    subscribeHandler,
    reactivateSubscriptionHandler,
    formatMessage,
    formatDurationUnitName,
    formatDistanceBetweenDatesInDays,
    cancelSubscriptionHandler,
    viewPaymentMethodHandler,
    addPaymentMethodHandler,
    viewBillingHistoryHandler,
  ]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassMembershipContentProps = Readonly<{
  subscribeHandler: () => Promise<void>;
  viewPaymentMethodHandler: (paymentMethodId: string) => void;
  addPaymentMethodHandler: () => void;
  viewBillingHistoryHandler: () => void;
}>;
